import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { FieldError, useForm } from 'react-hook-form'
import {
  CompoundDto,
  CreateCompoundProductionStepAssignmentDto,
  CompoundProductionStepAssignmentDto,
  CompoundProductionStepDto,
} from '@services/dtos'
import { compoundProductionStepAssignmentSchema } from '@utils/schemas'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  createCompoundProductionStepAssignment,
  updateCompoundProductionStepAssignment,
} from '@state/reducers/compoundProductionStepAssignmentReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { FormCrudHeader } from './FormCrudHeader'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import CompoundForm from './CompoundForm'
import { compoundService } from '@services/CompoundService'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { DataGridPro, GridColumns, GridRowId, GridRowOrderChangeParams } from '@mui/x-data-grid-pro'
import {
  getCompoundProductionSteps,
  setCompoundProductionStepFilters,
} from '@state/reducers/compoundProductionStepReducer'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { compoundProductionStepColumns } from '@utils/gridColumns'
import { DEFAULT_FILTERS } from '@state/reducers/baseCrudReducer'
import DeleteIcon from '@mui/icons-material/Delete'
import { Search } from 'lucide-react'
import { UpTransition } from '../animations'
import CompoundProductionStepForm from './CompoundProductionStepForm'

type Props = {
  editingCompoundProductionStepAssignment: CompoundProductionStepAssignmentDto | null
  onClose: () => void
}

const CompoundProductionStepAssignmentForm: React.FC<Props> = ({
  editingCompoundProductionStepAssignment,
  onClose,
}) => {
  const {
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CreateCompoundProductionStepAssignmentDto>({
    defaultValues: editingCompoundProductionStepAssignment
      ? {
          compoundId: editingCompoundProductionStepAssignment.compound.id,
          stepIds: editingCompoundProductionStepAssignment.steps.map(
            ({ id }) => id,
          ),
        }
      : {},
    resolver: yupResolver(compoundProductionStepAssignmentSchema),
  })

  const dispatch = useAppDispatch()
  const { steps } = useAppSelector(({ compoundProductionStep }) => ({
    steps: compoundProductionStep.items,
  }))

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [stepsSelected, setStepsSelected] = useState<
    CompoundProductionStepDto[]
  >([])
  const [compoundProductionStepFormOpen, setCompoundProductionStepFormOpen] =
    useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    if (editingCompoundProductionStepAssignment?.steps) {
      setStepsSelected(editingCompoundProductionStepAssignment.steps)
    }

    dispatch(setCompoundProductionStepFilters({ limit: 10 }))
    dispatch(getCompoundProductionSteps())
    return () => {
      reset()
      dispatch(setCompoundProductionStepFilters(DEFAULT_FILTERS))
    }
  }, [dispatch])

  const onSubmitForm = async (
    data: CreateCompoundProductionStepAssignmentDto,
  ) => {
    setIsSubmitting(true)
    const displayErrorMessage = () => cb('Une erreur est survenue')

    if (editingCompoundProductionStepAssignment) {
      dispatch(
        updateCompoundProductionStepAssignment({
          id: editingCompoundProductionStepAssignment.id,
          dto: data,
        }),
      )
        .unwrap()
        .then(() => {
          cb('L\'affectation d\'étapes de production a été modifié avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createCompoundProductionStepAssignment(data))
        .unwrap()
        .then(() => {
          cb('L\'affectation d\'étapes de production a été créé avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    }
  }

  const fetchCompounds = async (search: string) => {
    const page = await compoundService.getAll({ search, isProductionStepConfigurable: true })
    const list = page?.datas ?? []
    return list
  }

  const handleRowOrderChange = (params: GridRowOrderChangeParams) => {
    const newRows = [...stepsSelected]
    const [moved] = newRows.splice(params.oldIndex, 1)
    newRows.splice(params.targetIndex, 0, moved)
    setStepsSelected(newRows)
  }

  // setTimeout : fix No row with id #[...] found
  const removeStep = (id: GridRowId) => {
    setTimeout(() => {
      setStepsSelected((prevRows) => prevRows.filter((row) => row.id !== id))
    })
  }

  const stepsColumns: GridColumns = [
    {
      field: 'actions',
      headerName: '',
      width: 55,
      renderCell: ({ row }) => {
        const isSelected = stepsSelected.map(({ id }) => id).includes(row.id)
        return (
          <Tooltip
            title={
              isSelected ? 'Retirer de la sélection' : 'Ajouter à la sélection'
            }
            arrow
          >
            <IconButton
              onClick={() =>
                isSelected
                  ? removeStep(row.id)
                  : setStepsSelected((prev) => [...prev, row])
              }
            >
              {isSelected ? (
                <CheckBoxIcon color="primary" />
              ) : (
                <CheckBoxOutlineBlankIcon />
              )}
            </IconButton>
          </Tooltip>
        )
      },
    },
    ...compoundProductionStepColumns,
  ]
  const stepsSelectedColumns: GridColumns = [
    {
      field: 'actions',
      headerName: '',
      width: 35,
      renderCell: ({ row }) => (
        <Tooltip title="Retirer de la sélection" arrow>
          <IconButton onClick={() => removeStep(row.id)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ),
    },
    ...compoundProductionStepColumns,
  ]

  const handlePreSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setValue(
      'stepIds',
      stepsSelected.map(({ id }) => id),
    )
    handleSubmit(onSubmitForm)()
  }

  return (
    <div className="flex w-full h-full p-4">
      <form onSubmit={handlePreSubmit} className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingCompoundProductionStepAssignment}
        />

        <div className="w-full mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Affectation étapes production
            </legend>

            <Grid container columnSpacing={2} mt={1}>
              <Grid item xs={12} sm={6}>
                <AddWrapperInput
                  legend="MRP"
                  addTooltipTitle="Ajouter un MRP"
                  dialogContent={(close) => (
                    <CompoundForm onClose={close} />
                  )}
                  input={
                    <ServerAutocomplete<CompoundDto>
                      fetchService={fetchCompounds}
                      onChange={(value: CompoundDto | null) => {
                        setValue('compoundId', value?.id ?? '')
                      }}
                      getValue={(value) => value?.id?.toString() || null}
                      getOptionLabel={(value) => {
                        const compound = value as CompoundDto
                        return compound ? compound.name : ''
                      }}
                      debounceMs={300}
                      placeholder="Rechercher un MRP"
                      label="MRP"
                      helperText={errors.compoundId?.message}
                      error={!!errors.compoundId}
                      minSearchLength={0}
                      initialOptions={
                        editingCompoundProductionStepAssignment?.compound
                          ? [editingCompoundProductionStepAssignment.compound]
                          : []
                      }
                      value={
                        editingCompoundProductionStepAssignment?.compound ??
                        null
                      }
                    />
                  }
                />

                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5, mt: 1 }}>
                  Étapes sélectionnées
                </Typography>
                <DataGridPro
                  density="standard"
                  style={{
                    height: '40vh',
                    backgroundColor: 'white',
                    borderRadius: '15px 15px 25px 25px',
                  }}
                  headerHeight={26}
                  rowHeight={36}
                  columns={stepsSelectedColumns}
                  rows={stepsSelected}
                  rowReordering
                  onRowOrderChange={handleRowOrderChange}
                  sx={{
                    '& .MuiDataGrid-row--dragging .MuiDataGrid-rowReorderCellPlaceholder': {
                      display: 'none',
                    },
                    '& .MuiDataGrid-cell[data-field="actions"]': {
                      paddingLeft: 0,
                      paddingRight: 0,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {errors.stepIds && (
                  <FormHelperText error>
                    {(errors.stepIds as unknown as FieldError).message}
                  </FormHelperText>
                )}
                <Box display="flex" alignItems="center" className="mb-2">
                  <TextField
                    label="Recherche étape"
                    placeholder="Rechercher un libellé ou code"
                    onChange={(e) => {
                      dispatch(
                        setCompoundProductionStepFilters({
                          search: e.target.value,
                          page: 0,
                        }),
                      )
                      dispatch(getCompoundProductionSteps())
                    }}
                    InputProps={{
                      endAdornment: (
                        <Search className={'text-gray-500'} size={20} />
                      ),
                    }}
                    className="w-full rounded-xl bg-gray-50 transition-all duration-200 hover:bg-gray-100 focus:bg-white"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgb(229, 231, 235)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          boxShadow: '0 0 0 2px rgb(229 231 235)',
                        },
                      },
                    }}
                  />
                  <span className="inline-flex items-center">
                    <Tooltip title="Ajouter une étape" arrow>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setCompoundProductionStepFormOpen(true)}
                        sx={{
                          height: 55,
                          paddingLeft: 1.5,
                          paddingRight: 1.5,
                          minWidth: 'auto',
                          width: 'auto',
                          borderColor: '#e5e7eb',
                        }}
                      >
                        <i className="fas fa-plus"></i>
                      </Button>
                    </Tooltip>
                  </span>
                </Box>
                <DataGridPro
                  density="standard"
                  style={{
                    height: '48.7vh',
                    backgroundColor: 'white',
                    borderRadius: '15px 15px 25px 25px',
                  }}
                  headerHeight={26}
                  rowHeight={36}
                  className="mb-4 mt-2"
                  columns={stepsColumns}
                  rows={steps.datas}
                  rowCount={steps.totalCount}
                  disableSelectionOnClick
                  pagination
                  paginationMode="server"
                  disableMultipleSelection
                  pageSize={10}
                  onPageChange={(page) => {
                    dispatch(setCompoundProductionStepFilters({ page }))
                    dispatch(getCompoundProductionSteps())
                  }}
                />
              </Grid>
            </Grid>
          </fieldset>
        </div>
      </form>

      {compoundProductionStepFormOpen && (
        <Dialog
          open={compoundProductionStepFormOpen}
          onClose={() => setCompoundProductionStepFormOpen(false)}
          TransitionComponent={UpTransition}
          slotProps={{ backdrop: {} }}
        >
          <DialogContent>
            {compoundProductionStepFormOpen && (
              <CompoundProductionStepForm
                onClose={() => setCompoundProductionStepFormOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

export default CompoundProductionStepAssignmentForm
